import React from 'react';
import {Alert, Box, Button, Checkbox, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import {formatSegmentsInDocumentsSummary} from './formatSegmentsInDocumentsSummary';
import {formatDate} from '../../../../../utils/formatTimestamp';

export default function DeltaStoriesView({
                                             isProductLoading = false,
                                             selectedEntity,
                                             deltaBaselineDate,
                                             storiesLoading,
                                             storiesError,
                                             stories,
                                             selectedClusterIds,
                                             onClusterToggle,
                                             creatingPitch,
                                             onCreatePitch,
                                             createPitchError,
                                             createPitchSuccess,
                                             onOpenCreatedPitch,
                                             onCloseCreatePitchSuccess,
                                             onBack,
                                             onStoryOpen,
                                             onRefreshDeltaRun,
                                             refreshDisabled = false,
                                         }) {
    const baselineDateLabel = React.useMemo(() => {
        if (!deltaBaselineDate) return 'selected date';
        return formatDate(deltaBaselineDate, deltaBaselineDate);
    }, [deltaBaselineDate]);

    const showEntityHeader = Boolean(selectedEntity?.label);

    return (
        <Stack spacing={2} sx={{p: 2}}>
            {(onBack || onRefreshDeltaRun) ? (
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{width: '100%'}}>
                    {onBack ? (
                        <Button
                            type="button"
                            startIcon={<ArrowBackIcon/>}
                            onClick={onBack}
                            sx={{alignSelf: 'flex-start'}}
                        >
                            Back
                        </Button>
                    ) : (
                        <Box/>
                    )}
                    {onRefreshDeltaRun ? (
                        <Tooltip title="New delta run (separate LLM audit)">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={onRefreshDeltaRun}
                                    disabled={refreshDisabled || storiesLoading || creatingPitch}
                                    aria-label="New delta run"
                                >
                                    <RefreshIcon fontSize="small"/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    ) : null}
                </Stack>
            ) : null}

            {showEntityHeader ? (
                <>
                    <Typography variant="h6">{selectedEntity.label}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedEntity?.mentionsCount ?? 0} mentions since {baselineDateLabel}.
                    </Typography>
                </>
            ) : null}

            {isProductLoading && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18}/>
                    <Typography variant="body2">Waiting for product to load</Typography>
                </Stack>
            )}

            {!isProductLoading && storiesLoading && (
                <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18}/>
                    <Typography variant="body2">Loading delta stories...</Typography>
                </Stack>
            )}

            {storiesError && <Alert severity="error">{storiesError}</Alert>}

            {!isProductLoading && !storiesLoading && !storiesError && stories.length === 0 && (
                <Typography variant="body2">No delta stories returned yet.</Typography>
            )}

            {!isProductLoading && !storiesLoading && stories.length > 0 && (
                <Stack spacing={1.5} sx={{pb: selectedClusterIds.size > 0 ? 9 : 0}}>
                    {stories.map((story) => (
                        <Box
                            key={story.clusterId}
                            onClick={() => onStoryOpen(story)}
                            sx={{
                                position: 'relative',
                                p: 1.5,
                                border: (theme) => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'action.hover',
                                },
                            }}
                        >
                            <Checkbox
                                checked={selectedClusterIds.has(String(story.clusterId))}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => onClusterToggle(story, event.target.checked)}
                                sx={{position: 'absolute', top: 4, right: 4}}
                            />
                            <Typography
                                variant="subtitle2"
                                fontWeight={600}
                                sx={{
                                    paddingRight: 5
                                }}
                            >{story.title}</Typography>
                            <Stack direction="row" flexWrap="wrap" sx={{mt: 0.5, gap: 0.5}}>
                                {story.significance != null && Number.isFinite(Number(story.significance)) ? (
                                    <Chip size="small" label={`Significance ${story.significance}`} variant="outlined"/>
                                ) : null}
                                {(Array.isArray(story.targetEntityLabels) ? story.targetEntityLabels : []).map((label, i) => (
                                    <Chip key={`${label}-${i}`} size="small" label={label} variant="outlined"/>
                                ))}
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                {story.description}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                                {formatSegmentsInDocumentsSummary(story.segments)}
                            </Typography>
                        </Box>
                    ))}
                </Stack>
            )}

            {selectedClusterIds.size > 0 && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        bgcolor: 'background.paper',
                        pt: 1,
                        mt: 'auto',
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {createPitchSuccess ? (
                        <Alert
                            severity="success"
                        >
                            <Stack>

                                New pitch created.
                            </Stack>
                            <Stack direction={'row'} spacing={1} pt={2}>
                                <Button size="small" onClick={onOpenCreatedPitch}
                                        variant={'contained'}
                                        color={'success'}
                                >Open in new tab</Button>
                                <Button size="small" color="inherit"
                                        onClick={onCloseCreatePitchSuccess}>Close</Button>
                            </Stack>
                        </Alert>
                    ) : (
                        <Stack spacing={1}>
                            {createPitchError && <Alert severity="error">{createPitchError}</Alert>}
                            <Button
                                variant="contained"
                                onClick={onCreatePitch}
                                disabled={creatingPitch}
                                fullWidth
                            >
                                {creatingPitch
                                    ? 'Creating pitch...'
                                    : `Create new pitch (${selectedClusterIds.size})`}
                            </Button>
                        </Stack>
                    )}
                </Box>
            )}
        </Stack>
    );
}

